import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin } from "lucide-react";
import { Reveal, MaskedLines, Overline } from "@/components/Motion";
import { EnquiryForm } from "@/pages/Services";
import { useSettings, usePage } from "@/settings";

function useContactRows() {
  const s = useSettings();
  const digits = (s.contact_phone || "").replace(/\D/g, "");
  const wa = digits.length === 10 ? `https://wa.me/91${digits}` : `https://wa.me/${digits}`;
  return [
    { icon: Mail, label: "Email", value: s.contact_email, href: `mailto:${s.contact_email}`, testid: "contact-email" },
    { icon: Phone, label: "Phone / WhatsApp", value: s.contact_phone, href: wa, testid: "contact-phone" },
    { icon: Instagram, label: "Instagram", value: s.contact_instagram, href: `https://www.instagram.com/${(s.contact_instagram || "").replace(/^@/, "").replace(/\/$/, "")}/`, testid: "contact-instagram" },
    { icon: MapPin, label: "Studio", value: s.contact_location, href: null, testid: "contact-location" },
  ];
}

export default function Contact() {
  const rows = useContactRows();
  const pg = usePage("contact");
  return (
    <main data-testid="contact-page" className="pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
      <Overline>Contact</Overline>
      <MaskedLines
        className="mt-6 mb-20"
        lineClassName="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight"
        lines={[pg.heading_1 || "Say hello."]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p className="text-ink/60 leading-relaxed max-w-md mb-12">
            {pg.intro || "Architecture enquiries, artwork commissions, workshop questions and collaborations — write directly, or use the form and the studio will respond within two working days."}
          </p>
          <ul className="space-y-0">
            {rows.map((r) => (
              <li key={r.label} className="border-t border-ink/15 last:border-b">
                {r.href ? (
                  <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                    data-testid={r.testid}
                    className="group flex items-center justify-between py-5 hover:text-brand transition-colors">
                    <span className="flex items-center gap-4">
                      <r.icon size={16} className="text-brand" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40 group-hover:text-brand">{r.label}</span>
                    </span>
                    <span className="font-display text-lg">{r.value}</span>
                  </a>
                ) : (
                  <div data-testid={r.testid} className="flex items-center justify-between py-5">
                    <span className="flex items-center gap-4">
                      <r.icon size={16} className="text-brand" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">{r.label}</span>
                    </span>
                    <span className="font-display text-lg">{r.value}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {pg.map_link && (
            <a href={pg.map_link} target="_blank" rel="noreferrer" data-testid="contact-map-link"
              className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand link-line">
              View studio on map →
            </a>
          )}
          <Link to="/careers" data-testid="contact-careers-link"
            className="group mt-10 border border-ink/15 hover:border-brand transition-colors p-5 flex items-center justify-between gap-4">
            <span className="text-sm text-ink/60">For careers, visit the Careers page.</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">Careers →</span>
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="bg-linen border border-ink/10 p-8 md:p-12">
            <h2 className="font-display text-3xl tracking-tight mb-10">Write to the studio</h2>
            <EnquiryForm testidPrefix="contact" />
          </div>
        </Reveal>
      </div>
    </main>
  );
}
